import type { PermissionCode } from '@cnss/shared';
import { ROLE_LABELS } from '@cnss/shared';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  AlertTriangle,
  Briefcase,
  Building2,
  CreditCard,
  FlaskConical,
  History,
  IdCard,
  LayoutDashboard,
  LayoutTemplate,
  LogOut,
  Menu,
  Printer,
  PackageSearch,
  ScanLine,
  Settings,
  ShieldAlert,
  ShieldCheck,
  Truck,
  UserCheck,
  Users,
  X,
} from 'lucide-react';
import type { ComponentType } from 'react';
import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { usePublicConfigQuery } from '@/features/config/api';
import { useAuth } from '@/lib/auth-context';
import { cn } from '@/lib/cn';

interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  /** Lien masqué si l'utilisateur n'a pas cette permission — la garde d'accès réelle reste RequireAuth. */
  permission?: PermissionCode;
  /** Lien masqué si la fonctionnalité n'est pas activée côté serveur (voir /config/public). */
  requiresFeature?: 'testZone';
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Tableau de bord', icon: LayoutDashboard },
  {
    to: '/referentiel/employeurs',
    label: 'Employeurs',
    icon: Briefcase,
    permission: 'referentiel.view',
  },
  {
    to: '/referentiel/travailleurs',
    label: 'Travailleurs',
    icon: IdCard,
    permission: 'referentiel.view',
  },
  { to: '/utilisateurs', label: 'Utilisateurs', icon: Users, permission: 'users.manage' },
  { to: '/roles', label: 'Rôles & permissions', icon: ShieldCheck, permission: 'roles.manage' },
  { to: '/audit', label: "Journal d'audit", icon: History, permission: 'audit.view' },
  { to: '/biometrie/file-attente', label: "File d'attente biométrie", icon: UserCheck, permission: 'biometrie.capture' },
  { to: '/abis/revue', label: 'Revue ABIS', icon: ShieldAlert, permission: 'abis.review' },
  { to: '/cartes/lots', label: 'Lots de cartes', icon: CreditCard, permission: 'carte.personnaliser' },
  { to: '/impression/lots', label: 'Impression par lot', icon: Printer, permission: 'carte.personnaliser' },
  { to: '/parametres/carte-modeles', label: 'Modèles de carte', icon: LayoutTemplate, permission: 'carte.design' },
  { to: '/parametres/sites', label: 'Sites opérationnels', icon: Building2, permission: 'sites.manage' },
  { to: '/logistique/mouvements', label: 'Mouvements logistiques', icon: Truck, permission: 'logistique.manage' },
  { to: '/logistique/stock', label: 'Stocks de cartes', icon: PackageSearch, permission: 'logistique.manage' },
  { to: '/logistique/incidents', label: 'Incidents logistiques', icon: AlertTriangle, permission: 'logistique.manage' },
  { to: '/controle', label: 'Contrôle de carte', icon: ScanLine, permission: 'controle.effectuer' },
  {
    to: '/zone-test',
    label: 'Zone de test',
    icon: FlaskConical,
    permission: 'testzone.manage',
    requiresFeature: 'testZone',
  },
  { to: '/parametres/materiel', label: 'Paramètres du poste', icon: Settings },
];

/**
 * Ossature de l'espace authentifié : en-tête institutionnel + navigation
 * latérale (liens filtrés par permission via `hasPermission`, et pour la Zone
 * de test, par la configuration publique du serveur — jamais devinée côté
 * client, voir apps/api/src/modules/config/config.routes.ts) + zone de
 * contenu. Les modules métier des sprints suivants ajoutent leurs entrées à
 * `NAV_ITEMS`.
 */
export function AppShell() {
  const { user, logout, hasPermission } = useAuth();
  const { data: publicConfig } = usePublicConfigQuery();
  const visibleItems = NAV_ITEMS.filter((item) => {
    if (item.permission && !hasPermission(item.permission)) return false;
    if (item.requiresFeature === 'testZone' && !publicConfig?.featureTestZoneEnabled) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-muted/20">
      <header className="border-b border-border bg-card">
        <div className="container flex h-16 items-center justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <MobileNav items={visibleItems} />
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
              <ShieldCheck className="h-5 w-5" aria-hidden />
            </div>
            {/* Sous-titre masqué sous `sm` : à 375px, en-tête + tiroir + bloc
                utilisateur n'ont pas la place d'afficher les deux lignes sans
                un retour à la ligne disgracieux (voir Sprint 7). */}
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold leading-none">Carte Sociale CNSS</p>
              <p className="hidden text-xs text-muted-foreground sm:block">Plateforme nationale</p>
            </div>
          </div>
          {user && (
            <div className="flex shrink-0 items-center gap-4">
              {/* Identité/rôle masqués sous `sm`, même raison que ci-dessus —
                  toujours consultable dans le tiroir de navigation mobile. */}
              <div className="hidden text-right sm:block">
                <p className="text-sm font-medium leading-none">{user.nomComplet}</p>
                <p className="text-xs text-muted-foreground">{ROLE_LABELS[user.role]}</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => void logout()}
                aria-label="Déconnexion"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </header>
      <div className="container flex gap-8 py-8">
        <nav className="hidden w-56 shrink-0 space-y-1 md:block" aria-label="Navigation principale">
          {visibleItems.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.to === '/'} className={navLinkClassName}>
              <item.icon className="h-4 w-4" aria-hidden />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <main className="min-w-0 flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function navLinkClassName({ isActive }: { isActive: boolean }): string {
  return cn(
    'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors',
    isActive ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-secondary',
  );
}

/**
 * Navigation mobile (Sprint 7) : la barre latérale (`nav` ci-dessus) est
 * masquée sous `md` (`hidden md:block`) sans aucune alternative jusqu'ici —
 * un problème concret sur les canaux "mobile" pourtant exigés par le cahier
 * des charges (notamment la vérification de carte, `/controle`). Ce tiroir,
 * construit directement sur `@radix-ui/react-dialog` (déjà en dépendance,
 * utilisé par ailleurs pour les boîtes de dialogue centrées de
 * `components/ui/dialog.tsx`) plutôt que sur ce composant, prend une
 * présentation de panneau latéral au lieu d'une modale centrée, mais reste
 * cohérent avec le reste du design (mêmes couleurs, mêmes icônes, mêmes
 * classes de lien que la navigation de bureau via `navLinkClassName`).
 * Entièrement masqué en dehors de `md` (bouton déclencheur ET contenu),
 * jamais rendu en même temps que la barre latérale de bureau. Se referme
 * après tout clic sur un lien, comme attendu d'un tiroir de navigation.
 */
function MobileNav({ items }: { items: NavItem[] }) {
  const [open, setOpen] = useState(false);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Trigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden" aria-label="Ouvrir le menu de navigation">
          <Menu className="h-5 w-5" aria-hidden />
        </Button>
      </DialogPrimitive.Trigger>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-[1px] md:hidden" />
        <DialogPrimitive.Content
          className={cn(
            'fixed inset-y-0 left-0 z-50 flex w-72 max-w-[80vw] flex-col gap-1 overflow-y-auto',
            'border-r border-border bg-card p-4 shadow-lg focus:outline-none md:hidden',
          )}
          aria-describedby={undefined}
        >
          <div className="mb-3 flex items-center justify-between">
            <DialogPrimitive.Title className="text-sm font-semibold text-muted-foreground">
              Navigation
            </DialogPrimitive.Title>
            <DialogPrimitive.Close asChild>
              <Button variant="ghost" size="icon" aria-label="Fermer le menu">
                <X className="h-4 w-4" aria-hidden />
              </Button>
            </DialogPrimitive.Close>
          </div>
          <nav className="flex flex-col gap-1" aria-label="Navigation principale">
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={navLinkClassName}
                onClick={() => setOpen(false)}
              >
                <item.icon className="h-4 w-4" aria-hidden />
                {item.label}
              </NavLink>
            ))}
          </nav>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
